import { PRODUCT_IDS } from "@/lib/booking"
import { membershipTierAmount, type PriceMap } from "@/lib/pricing"
import { membershipFirstSiteEnabled } from "@/lib/release-flags"

/**
 * What the homepage v3 draft needs that is NOT copy: prices, tile geometry,
 * the sources behind section 02's figures, logos, icon names, counts and a
 * few proper nouns. The copy itself lives in the `homeV4` namespace of the
 * message catalogues (en, de, fr) and is read by `useHomeV4Copy()` in
 * `home-v4-copy.ts`, which merges the two back into the per-section shapes
 * the components were written against.
 */

/**
 * Membership entry price: the once-a-year rhythm's yearly price from Stripe
 * (get-products' `membershipTiers`, price key `membershipOnce`), 299 when the
 * feed has none. Pass the page's server-read prices (`planData.initialPrices`)
 * so the server render and hydration show the same figure.
 */
export function membershipAnnualAmount(
  prices: PriceMap | null | undefined
): number {
  return membershipTierAmount(prices, "once")
}

/**
 * WHERE THE BUTTONS GO. Every booking CTA on this draft opens the local
 * booking APP on the membership-first branch, not the HTML prototype it used
 * to open (08.09): the page and the flow behind it are being designed side by
 * side and Eugene wants to walk from one into the other end to end.
 * The chosen check-up rides in the booking app's own link contract,
 * `products=` (the package's id) and `packageType`, the same the live CTAs
 * send (lib/booking.ts), and the baseline block's MRI toggle is what picks
 * it: the app attaches the Full-body MRI to the plan. Served by `(3054)
 * membership-first` locally. Deployed builds (the PR #2663 preview Eugene
 * shares, 15.09) open the public Vercel preview of the pushed
 * `eugene/membership-first-v2` commit 7e197283 instead, since nobody else can
 * reach localhost. Bump that URL after a new booking push. The real URLs come
 * back the moment `overrideHref` is dropped from the four CTAs.
 */
const BOOKING_APP =
  process.env.NODE_ENV === "development"
    ? "http://localhost:3054"
    : "https://ahead-booking-nz3r9zz8q-ahead-healths-projects.vercel.app"
export type BookingScan = "blood" | "core" | "mri" | "pro"
const SCAN_PRODUCT: Record<BookingScan, string> = {
  blood: PRODUCT_IDS.bloodTest,
  core: PRODUCT_IDS.core,
  mri: PRODUCT_IDS.advanced,
  pro: PRODUCT_IDS.pro,
}
export function bookingFlowUrl(
  scan: BookingScan = "blood",
  addOnIds: readonly string[] = []
) {
  // With the membership-first site on (RELEASE_MEMBERSHIP_FIRST), the CTAs drop
  // the override and BookingCta routes them with locale, market and the same
  // `products`. The flag alone is the right question here, not
  // `isMembershipFirstSite(country)`: BookingCta's flow is already per market
  // (`useBookingFlow`), so CH opens the membership-first app and a market
  // outside the gate the current one. Asking for the country would send that
  // market to the prototype below instead.
  if (membershipFirstSiteEnabled()) return undefined
  const params = new URLSearchParams({
    products: [SCAN_PRODUCT[scan], ...addOnIds].join(","),
    packageType: scan === "blood" ? "blood-only" : "mri",
    from: "home",
  })
  return `${BOOKING_APP}/?${params.toString()}`
}

/**
 * BILLING PERIOD — the Figma button reads per month. Everything shipped says
 * per year: the nav, the homepage, the membership hero KPI, and the
 * membership T&Cs, which define it as "an automatic, recurring annual
 * membership payment". Saying per month would overstate the price twelvefold,
 * so the draft says per year (`homeV4.membershipPeriod`). If a monthly plan
 * really is coming, that key is the one line to change.
 */

/** 01 — Today is a good day to start. */
export const WHY_NOW_DATA = {
  // Ambassador quote. DE is Laura's own wording from her interview clips for
  // Ahead (Google Doc "Laura_Philipp_Interview_Clips_QA", overall clip, July
  // 2026); EN and FR are our translations. Role wording matches
  // /go/lauraphilipps.
  author: "Laura Philipp",
} as const

/**
 * 03 — Sound familiar? Four glass cards over the warm photograph that used
 * to carry the statistics ("Most of what matters changes quietly",
 * Figma 303:5853). The structure stays; the content is the draft's concern
 * lines (Eugene, 18.09): the worry in the reader's words, then what we look
 * at. The 23% / 62% / 1-in-4 figures are parked with the draft. No source
 * links on the cards (Eugene, 18.09: no other section links out, so this one
 * should not either); behind the Lp(a) row are Gelfert (JACC Adv 2025,
 * doi 10.1016/j.jacadv.2025.102015) and Stürzebecher (Atherosclerosis 2023,
 * doi 10.1016/j.atherosclerosis.2023.01.014). The share is 21.6% ≥50 mg/dL in
 * GNHIES98 ("1 in 5"); "diet does not lower" because a low-carb,
 * high-saturated-fat diet does move it — downwards, and nobody would
 * prescribe it. Since 24.09 (Eugene: unslop, optimistic, actionable) row two
 * no longer shows the one-in-five figure; it says what ApoB and Lp(a) add and
 * that the doctor shows how to lower the risk. Lp(a) itself barely moves, so
 * the promise is the overall risk, not the value. Row four's "1 in 5 scans" is the live MRI page's own claim;
 * its wording is still to be agreed with Sven or Dennis.
 */
export const FAMILIAR_DATA = {
  /**
   * Approved editorial lifestyle images, stored on Blob. Focal positions
   * keep the people in frame when narrow screens crop the landscape photos.
   */
  rows: [
    {
      image:
        "/images/marketing/home-v4/home-v4-familiar-01-rest-brunette-20261012.webp",
      position: "75% 50%",
    },
    {
      // Reshot on a monochrome studio set, walking down the steps; Eugene
      // picked the teal take (13.10). Blue and terracotta sit beside it in
      // Blob.
      image:
        "/images/marketing/home-v4/home-v4-familiar-02-heart-studio-teal-20261013.webp",
      position: "75% 50%",
    },
    {
      image: "/images/marketing/home-v4/selected-local/03-family-lakeside.png",
      position: "85% 50%",
    },
    {
      image:
        "/images/marketing/home-v4/home-v4-familiar-04-clarity-blonde-20261012.webp",
      position: "70% 50%",
    },
  ],
} as const

/**
 * 03b — Compared. A feature column, then Ahead, then a standard check-up,
 * laid out like the NOA table Eugene sent (22.09). Row 1 compares a count,
 * so both of its cells are words. "check" rows are a check against a dash;
 * "text" rows are a check against a short phrase, used wherever a GP may
 * still order the test, so the cell says when rather than "no". Every
 * claim comes from the live compare page (compareHealthCheckUps: 15–20
 * markers, ApoB and fasting insulin not in a GP panel, TSH only if
 * indicated, no imaging without symptoms), so it is no newer than what is
 * already public (Eugene, 22.09: harder to question). Rows 5, 7 and 8 are
 * new: biological age (our report calculates it from the blood markers), the
 * written plan against a talk in the visit, and planned retests (the
 * follow-up blood test add-on) against a retest only when a value is off.
 * Still one check with Sven or Dennis (UWG on comparative advertising: no
 * named competitors).
 */
export const COMPARE_DATA = {
  // `detail`: a grey line under the label naming what the check covers
  // (Eugene, 07.10: the bare "we include X" read flat). Rows 2 and 3 were
  // ApoB and insulin on their own; they are one heart-and-metabolism row now,
  // and the online report with trends takes the freed row. Row 3 is iron
  // with inflammation, not the thyroid, and row 5 names the MRI's reach,
  // "12 organ systems", in Ahead's cell (`ahead`) instead of a check
  // (Sven, 08.10).
  rows: [
    { kind: "count", detail: false },
    { kind: "text", detail: true },
    { kind: "text", detail: true },
    { kind: "check", detail: false },
    { kind: "text", detail: true, ahead: true },
    { kind: "check", detail: true },
    { kind: "text", detail: true },
    { kind: "text", detail: false },
  ],
} as const

/**
 * 03 — A full view of your health. Figma 306:7659 (the bordered card),
 * 310:8213 (the four columns) and the 128px tiles that sit half over the
 * card's top edge. The four eyebrows are four STAGES, re-cut after the
 * 09.09 daily (Nick, Michèle, Laurent): what you bring → baseline blood →
 * imaging, optional, MRI and DEXA as ONE card → every year. The wireframe's
 * separate "added for you" DEXA column made the MRI read as a mandatory
 * step and the section as "do all of it or it means nothing" (Grégoire on
 * AHD-11317). Never more than four columns here. The MRI tile stays, the
 * DEXA one (tile-3) is retired; the first tile shows the existing
 * data-upload laptop image.
 */
export const FULL_VIEW_DATA = {
  columns: [
    { tile: "/images/marketing/home-v4/data-upload.webp" },
    { tile: "blood-tubes-close.webp" },
    { tile: "mri-bright.webp" },
    { tile: "trend-glass-cropped.webp" },
  ],
} as const

/**
 * 04 — Everything lands in one plan. Figma 314:9674, a 1221px dark band with
 * a photographic collage.
 *
 * `x`/`y`/`w` are the Figma frame's own coordinates, so the collage can be
 * laid out from the numbers rather than from eyeballed offsets. y is
 * relative to the top of the collage box (page y 3949).
 */
export const ONE_PLAN_DATA = {
  /** Boxes only — what goes inside each is `home-v4-plan-tiles.tsx`. */
  tiles: [
    { kind: "chat", x: 1056, y: 191, w: 378 },
    { kind: "hero", x: 467, y: 0, w: 570 },
    { kind: "phone", x: 68, y: 0, w: 380 },
    { kind: "mri", x: 275, y: 398, w: 168 },
    { kind: "hand", x: 1056, y: 0, w: 168 },
  ],
  trust: [{ icon: "vpn" }, { icon: "shield" }, { icon: "lock" }],
} as const

/**
 * 05b — What you get. A second immersive section, its own component
 * (`home-v4-what-you-get.tsx`) rather than a second run of 05 (Eugene,
 * 18.09: "a full duplicate, with the new content"). Same 1512×687 box and
 * the same Figma-pixel convention as 05, but four tiles in three columns,
 * each with its own height: plan · trend · findings · recheck. Order is the
 * order they arrive in. The findings counts are the demo member's (Thomas,
 * 46, fictional), the same 4 + 5 the phone screen in 05 adds up.
 */
export const WHAT_YOU_GET_DATA = {
  tiles: [
    { kind: "plan", x: 467, y: 0, w: 570, h: 570 },
    { kind: "trend", x: 68, y: 0, w: 380, h: 290 },
    { kind: "findings", x: 1056, y: 0, w: 378, h: 570 },
    { kind: "recheck", x: 68, y: 308, w: 380, h: 262 },
  ],
  findings: { critical: 0, minor: 4, observations: 5 },
  /** HbA1c in %, first check-up → follow-up → yearly check-up. The dashed
      line is the lower edge of the pre-diabetes range (5.7%). */
  trend: { points: [5.9, 5.7, 5.5], threshold: 5.7 },
} as const

/**
 * Section 04's collage tiles read most of their copy from `report.mockup*`
 * and `_membership.*` — the same keys the report and membership pages ship.
 * What is left is genuinely not copy: a proper noun, a member's first name
 * and the counts the screen adds up and prints.
 */
export const PLAN_TILES_DATA = {
  hero: {
    /** A proper noun: never translated, per the repo's convention. */
    doctorName: "Prof. Dr. med. Olivio Donati",
  },
  phone: {
    /** Fictional, per the no-real-patient-data rule. */
    memberName: "Thomas",
    /** Value and caption are one claim: 42 biologically, four years under a
        calendar 46. The app's own `report.mockup*` strings
        say "1 yr younger … (39)", so `homeV4.planBioAge*` override them. */
    ageValue: "42",
    findingsMinorCount: 4,
    findingsObservationCount: 5,
    /** Third card, below the fold of the tile. The design stops at findings;
        these are the report app's own demo figures (12 + 73 + 4 = 89). */
    biomarkerOptimal: 12,
    biomarkerInRange: 73,
    biomarkerOutOfRange: 4,
  },
} as const

/**
 * 05 — It all goes to one doctor. Figma 314:11761, a 906px white band: a lead
 * quote with a 108px portrait, a 3×2 grid of six doctor cards, and a row of
 * university marks.
 *
 * The people are NOT here. They are the CMS's featured reviewers, fetched in
 * `home-v4-page-data.ts` with `getReviewerExperts` — the same six the live homepage
 * shows (`_components/doctors-section.tsx`) — so names, bios and
 * photographs are the real ones and stay in step with it. The logos are the
 * site's Blob vectors; `ratio` is each file's viewBox width ÷ height, which
 * the CSS mask needs because a masked box has no intrinsic size of its own.
 */
export const DOCTORS_DATA = {
  lead: {
    /** The About page's studio portrait, not the CMS profile photo (Eugene,
        04.09). Used for Anna wherever she appears in the section. */
    photo: "/images/doctors/erat.jpeg",
  },
  /**
   * Two-line captions (Eugene, 04.09), `homeV4.doctorsCaption<Surname>`.
   * The name line already carries the title, so each is institution ·
   * specialisation, condensed from the CMS bios (`role`), which run to three
   * or four lines. Kept under ~64 characters so two lines of body-md hold in
   * the 254px text column at 1440 — in German and French as well. A featured
   * expert without an entry here shows the CMS bio. Anna's is her role today:
   * the CMS profile still says "prevention expert" until it is updated.
   */
  captionSurnames: [
    "Donati",
    "Fischer",
    "Battegay",
    "Visser",
    "Wiestler",
    "Komaromi",
    "Erat",
  ],
  /**
   * Figma 326:13375 — the same marks `mri-credibility.tsx` already ships.
   * `dy` lifts a mark whose ink sits low in its box: rasterised at 34px,
   * St.Gallen's alpha centroid is 4.8px below centre (its wordmark hangs
   * under the symbol), Oxford's and Karolinska's are within 0.2px.
   * `phone` is the mark's height on a phone, where the three share one line
   * and St.Gallen's long wordmark gives up the most (Eugene, 06.10).
   */
  logos: [
    {
      src: "/images/logos/oxford.svg",
      alt: "University of Oxford",
      ratio: 2251 / 662,
      dy: 0,
      phone: 26,
    },
    {
      src: "/images/logos/karolinska.svg",
      alt: "Karolinska Institutet",
      ratio: 65.5 / 26.654,
      dy: 0,
      phone: 28,
    },
    {
      src: "/images/logos/st-gallen.svg",
      alt: "University of St.Gallen",
      ratio: 108.831 / 22.21,
      dy: -5,
      phone: 18,
    },
  ],
} as const

/**
 * 06 — Get started with your baseline. Figma 314:10569: a 1286×671 card,
 * radius 32, centred on the page (not on the content column), over a
 * photographic ground. The geometry constants live in the component next to
 * the elements they position.
 */
export const BASELINE_DATA = {
  promises: [{ icon: "shield" }, { icon: "target" }, { icon: "stethoscope" }],
  /** Appears in the list when the MRI is switched on, second — the scan
      belongs beside the blood test it extends, not appended as an
      afterthought at the bottom. */
  includedMri: { highlight: false },
  /** The member-pricing row is the one the design lifts into a lozenge. */
  included: [
    { highlight: false },
    { highlight: false },
    { highlight: true },
    { highlight: false },
    { highlight: false },
  ],
} as const

/**
 * 07 — Insurance. Figma 314:10935, an 838px band, everything centred.
 *
 * The Figma pill cloud (328:13820) is NOT reproduced: three rows mixing AXA
 * and Concordia with seven repeated health-topic chips on stock photography
 * ("Muscle & Bone Health" over a flexing-arm photo, twice). It reads as a
 * layout study, not a claim about coverage. Real insurers, real logos, from
 * the CMS instead — see `home-v4-insurers.ts`. Copy: `homeV4.insurance*`.
 */

/**
 * 09 — Your rhythm (the draft's "practical-alt", Eugene's pick, 18.09): Day
 * 1 · Day 5–7 · As needed, optional · Every 12 months. A cadence, not a
 * calendar year; the follow-up is visibly optional in the copy itself.
 * Layout is still the deck below. Figma 328:13589 (eyebrow), 328:13590 (heading)
 * and the numbered list 328:13967, which came in from the positioning
 * wireframe with the wireframe's own type: Menlo 10.5 caps labels, 38.4px
 * figures, 14.5px body, and a 54px heading. Menlo is not wired into this
 * site at all (it would have rendered as the system mono), and none of
 * those sizes is a rung. Normalised onto the ladder — label-md, display-sm
 * (38, within half a pixel of 38.4), body-md, display-md-emphasized — which
 * is the "still needs designing" part Eugene flagged for this section.
 */
export const PRACTICAL_DATA = {
  steps: [
    { optional: false },
    { optional: false },
    { optional: true },
    { optional: false },
  ],
} as const

/**
 * 10b — Stories. A second rail under the reviews, in the text review's card
 * (Eugene, 18.09): an opening line, the start of the story in the person's
 * own words, then who they are. Three FICTIONAL placeholders until real,
 * consented cases exist; every card is labelled as fictional in the page's
 * language. The full story will live on its own page; until it exists the
 * cards open /reviews.
 */
export const STORIES_DATA = {
  stories: [{ id: "thomas" }, { id: "sandra" }, { id: "markus" }],
} as const

/**
 * 12 — Not sure where to start? Figma 336:1760, a 728px full-bleed close
 * over a cool blue garden-wall portrait with the man on the right.
 * The draft's close-alt (Eugene, 18.09): the
 * quiz leads, booking is the secondary, three trust ticks under the copy.
 *
 * The Google line is the logo and five stars only (Eugene, 18.09: no
 * figures there).
 */
export const CLOSE_DATA = {
  /* `n` is the closeFeature<n> message. "Cancel anytime" closes the row, it
     is no reason to join (Michèle, 09.10). */
  features: [
    { icon: "shield", n: 2 },
    { icon: "calendar", n: 3 },
    { icon: "check", n: 1 },
  ],
} as const
